import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { blogArticles } from '@/mocks/blogData';

export default function BlogArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = blogArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl text-foreground-950 mb-4">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist or has been moved.</p>
            <Link to="/" className="px-6 py-3 rounded-lg bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.metaTitle,
    description: article.description,
    author: { '@type': 'Organization', name: 'Vigorbuy Spreadsheet' },
    datePublished: article.date,
    dateModified: article.date,
    url: `https://vigorbuyspread.pics/blog/${article.slug}`,
    publisher: { '@type': 'Organization', name: 'Vigorbuy Spreadsheet', url: 'https://vigorbuyspread.pics/' },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: { '@type': 'Answer', text: faq.a },
    })),
  };

  return (
    <>
      <title>{article.metaTitle}</title>
      <meta name="description" content={article.description} />
      <meta name="keywords" content={article.keywords} />
      <link rel="canonical" href={`https://vigorbuyspread.pics/blog/${article.slug}`} />
      <meta name="robots" content="index, follow" />
      <meta property="og:title" content={article.metaTitle} />
      <meta property="og:description" content={article.description} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://vigorbuyspread.pics/blog/${article.slug}`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={article.metaTitle} />
      <meta name="twitter:description" content={article.description} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <Navbar />

      <main>
        <article>
          <header className="relative pt-32 pb-14 md:pt-40 md:pb-16 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-background-100 to-background-50"></div>
            <div className="absolute top-0 right-0 w-80 h-80 bg-primary-100/25 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent-100/20 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4"></div>

            <div className="relative max-w-3xl mx-auto px-4 md:px-6 text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Link to="/" className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">Home</Link>
                <span className="text-xs text-foreground-400">/</span>
                <Link to="/#blog" className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">Blog</Link>
                <span className="text-xs text-foreground-400">/</span>
                <span className="text-xs font-medium text-foreground-500">{article.category}</span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-950 leading-tight mb-5">
                {article.title}
              </h1>
              <p className="text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed mb-5">{article.description}</p>
              <div className="flex items-center justify-center gap-3 text-xs text-foreground-500">
                <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{article.date}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span className="flex items-center gap-1"><i className="ri-time-line"></i>{article.readTime}</span>
              </div>
            </div>
          </header>

          <div className="max-w-3xl mx-auto px-4 md:px-6 mb-8">
            <div className="rounded-2xl overflow-hidden">
              <img src={article.image} alt={article.title} className="w-full h-auto object-cover" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="prose-content">
              {article.sections.map((section, sIdx) => (
                <section key={sIdx} id={section.heading.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')} className="mb-10">
                  <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">{section.heading}</h2>
                  {section.content.map((para, pIdx) => (
                    <p key={pIdx} className="text-foreground-700 leading-relaxed mb-4">{para}</p>
                  ))}
                  {section.image && (
                    <div className="my-6 rounded-xl overflow-hidden">
                      <img src={section.image} alt={section.heading} className="w-full h-auto object-cover" />
                    </div>
                  )}
                </section>
              ))}

              <section className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">Frequently Asked Questions</h2>
                <div className="space-y-3">
                  {article.faqs.map((faq, idx) => (
                    <details key={idx} className="group bg-background-50 border border-background-200/60 rounded-xl overflow-hidden">
                      <summary className="px-5 py-4 flex items-center justify-between cursor-pointer list-none">
                        <h3 className="text-sm font-semibold text-foreground-900 pr-4">{faq.q}</h3>
                        <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0 group-open:bg-primary-100 transition-colors">
                          <i className="ri-add-line text-foreground-600 group-open:hidden text-xs"></i>
                          <i className="ri-subtract-line text-primary-600 hidden group-open:block text-xs"></i>
                        </div>
                      </summary>
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>

              <div className="flex flex-col sm:flex-row items-center gap-4 p-6 bg-primary-50 border border-primary-100/50 rounded-2xl mb-10">
                <div className="flex-1">
                  <h3 className="font-heading text-lg text-foreground-950 mb-1">Ready to Explore Vigorbuy Spreadsheet?</h3>
                  <p className="text-sm text-foreground-600">Visit the official website to browse resources and templates.</p>
                </div>
                <div className="flex gap-3 flex-shrink-0">
                  <Link to="/" className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-background-50 border border-background-200/60 text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Back to Home</Link>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">Visit Official Site</a>
                </div>
              </div>

              <nav className="border-t border-background-200/50 pt-6 mb-8" aria-label="Breadcrumb">
                <ol className="flex items-center gap-2 text-sm flex-wrap">
                  <li><Link to="/" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">Home</Link></li>
                  <li className="text-foreground-400"><i className="ri-arrow-right-s-line text-xs"></i></li>
                  <li><Link to="/#blog" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">Blog</Link></li>
                  <li className="text-foreground-400"><i className="ri-arrow-right-s-line text-xs"></i></li>
                  <li><span className="text-foreground-500 font-medium">{article.title}</span></li>
                </ol>
              </nav>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}